import React from 'react';

import type { DataTableColumn } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { DateCell } from '../components/Timestamp';
import { EMPTY_VALUE, formatNumber } from '../lib/formatters';

/** What the Templates cell needs: the names we have (any number) and the true total. */
export interface LensTemplateNames {
  names: string[];
  total: number;
}

/**
 * Up to two template names, then "+N". The member read carries every name, the
 * System Admin read only the first two, so the screen-reader text names the
 * rest only when it has them.
 */
export const LensTemplatesCell: React.FC<LensTemplateNames> = ({ names, total }) => {
  if (total === 0) return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
  const shown = names.slice(0, 2);
  const more = Math.max(0, total - shown.length);
  const rest = names.slice(shown.length);
  return (
    <span className="block truncate">
      {shown.join(', ')}
      {more > 0 ? (
        <>
          <span aria-hidden="true" className="text-muted-foreground"> +{formatNumber(more)}</span>
          <span className="sr-only">
            {rest.length === more
              ? ` and ${formatNumber(more)} more: ${rest.join(', ')}`
              : ` and ${formatNumber(more)} more`}
          </span>
        </>
      ) : null}
    </span>
  );
};

/**
 * One header, formatter and cell per lens fact, shared by Lenses and System
 * Admin → All Lenses. Builders fix key, header and render; pages set the rest
 * (DESIGN_SYSTEM.md §9 Record facts).
 */
export const lensCol = {
  name: <T extends { name: string }>(): DataTableColumn<T> => ({
    key: 'name',
    header: 'Name',
    render: (row) => row.name,
  }),

  visibility: <T extends { isPublic: boolean }>(): DataTableColumn<T> => ({
    key: 'visibility',
    header: 'Visibility',
    render: (row) => <StatusBadge status={row.isPublic ? 'public' : 'private'} />,
  }),

  templates: <T,>(get: (row: T) => LensTemplateNames): DataTableColumn<T> => ({
    key: 'templates',
    header: 'Templates',
    render: (row) => <LensTemplatesCell {...get(row)} />,
  }),

  updated: <T extends { updatedAt: string }>(): DataTableColumn<T> => ({
    key: 'updatedAt',
    header: 'Updated',
    className: 'whitespace-nowrap text-muted-foreground',
    render: (row) => <DateCell value={row.updatedAt} />,
  }),

  created: <T extends { createdAt: string }>(): DataTableColumn<T> => ({
    key: 'createdAt',
    header: 'Created',
    className: 'whitespace-nowrap text-muted-foreground',
    render: (row) => <DateCell value={row.createdAt} />,
  }),
};
